import { StatusBar, Style } from '@capacitor/status-bar';
import { registerPlugin, SystemBarType, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { isNativePlatform } from './platformService.js';

const NativeTheme = registerPlugin('NativeTheme');

const FALLBACK_COLORS = {
  light: '#ffffff',
  dark: '#171717',
};

function statusBarStyleForTheme(theme) {
  return theme === 'dark' ? Style.Dark : Style.Light;
}

function systemBarsStyleForTheme(theme) {
  return theme === 'dark' ? SystemBarsStyle.Dark : SystemBarsStyle.Light;
}

function readCssColor(token, fallback) {
  if (typeof window === 'undefined') return fallback;
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return value || fallback;
}

function updateThemeColor(color) {
  if (typeof document === 'undefined') return;
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', color);
}

function normalizeStatusBarHeight(height) {
  const rawHeight = Number(height);
  if (!Number.isFinite(rawHeight) || rawHeight <= 0) return null;

  const devicePixelRatio = typeof window === 'undefined'
    ? 1
    : Number(window.devicePixelRatio) || 1;

  if (devicePixelRatio > 1 && rawHeight > 80) {
    return Math.round(rawHeight / devicePixelRatio);
  }

  return Math.round(rawHeight);
}

function updateStatusBarCss({ theme, color, height }) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--native-status-bar-color', color);
  const statusBarHeight = normalizeStatusBarHeight(height);
  if (statusBarHeight) {
    root.style.setProperty('--native-status-bar-height', `${statusBarHeight}px`);
  }
  root.style.colorScheme = theme;
}

async function tryStatusBarCall(callback) {
  try {
    return await callback();
  } catch {
    return null;
  }
}

export async function configureStatusBar(resolvedTheme = 'light') {
  const theme = resolvedTheme === 'dark' ? 'dark' : 'light';
  const backgroundColor = readCssColor('--paper', FALLBACK_COLORS[theme]);
  updateThemeColor(backgroundColor);
  updateStatusBarCss({ theme, color: backgroundColor });

  if (!isNativePlatform()) return;

  await Promise.all([
    tryStatusBarCall(() => NativeTheme.setSystemBarTheme({
      color: backgroundColor,
      dark: theme === 'dark',
    })),
    tryStatusBarCall(() => StatusBar.setOverlaysWebView({ overlay: false })),
    tryStatusBarCall(() => StatusBar.setStyle({ style: statusBarStyleForTheme(theme) })),
    tryStatusBarCall(() => StatusBar.setBackgroundColor({ color: backgroundColor })),
    tryStatusBarCall(() => SystemBars.setStyle({
      style: systemBarsStyleForTheme(theme),
      bar: SystemBarType.StatusBar,
    })),
    tryStatusBarCall(() => SystemBars.setStyle({
      style: systemBarsStyleForTheme(theme),
      bar: SystemBarType.NavigationBar,
    })),
  ]);

  const info = await tryStatusBarCall(() => StatusBar.getInfo());
  updateStatusBarCss({ theme, color: backgroundColor, height: info?.height });
}
